import { AnimatePresence, motion } from 'motion/react'
import { ArrowLeft, Minus, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router'

import { ProductCard } from '../components/ProductCard'
import { Container } from '../components/ui'
import { useCatalog } from '../lib/catalog'
import { cx } from '../lib/cx'
import { hideProductWeight, translateProductDetails, translateProductMaterials, useI18n } from '../lib/i18n'

export function Product() {
  const { t } = useI18n()
  const { slug } = useParams()
  const { bySlug, products } = useCatalog()
  const product = slug ? bySlug(slug) : undefined
  const [view, setView] = useState(0)
  const [open, setOpen] = useState<number | null>(0)

  useEffect(() => setView(0), [slug])
  if (!product) return <Navigate to="/shop" replace />

  const suffix = product.slug.replaceAll('-', '_')
  const nameKey = `product_name_${suffix}`
  const shortKey = `product_short_${suffix}`
  const descriptionKey = `product_description_${suffix}`
  const productName = t(nameKey) === nameKey ? product.name : t(nameKey)
  const productShort = hideProductWeight(t(shortKey) === shortKey ? product.short : t(shortKey))
  const productDescription = t(descriptionKey) === descriptionKey ? product.description : t(descriptionKey)
  const productDetails = translateProductDetails(product.slug, product.details)
  const productMaterials = translateProductMaterials(product.slug, product.materials)
  const views = [
    { label: t('full_view'), style: {} },
    { label: t('close_view'), style: { transform: 'scale(1.6)', transformOrigin: '50% 40%' } },
    { label: t('texture_view'), style: { transform: 'scale(2.1)', transformOrigin: '50% 65%' } },
  ]
  const related = products.filter((p) => p.slug !== product.slug && (p.category === product.category || p.featured)).slice(0, 4)
  const sections = [
    ...(productDetails.length ? [{ title: t('details'), body: productDetails }] : []),
    ...(productMaterials ? [{ title: t('materials_storage'), body: [productMaterials] }] : []),
  ]
  const badgeKey = product.badge === 'New' ? 'badge_new_label' : product.badge === 'Bestseller' ? 'badge_bestseller_label' : 'badge_low_stock_label'

  return (
    <Container className="max-w-6xl px-4 pt-24 sm:px-6 sm:pt-28">
      <Link to="/shop" className="inline-flex min-h-11 items-center gap-1.5 text-[13.5px] text-muted transition-colors hover:text-forest">
        <ArrowLeft className="size-4" /> {t('back_to_products')}
      </Link>

      <div className="mt-5 grid gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
        <div className="grid grid-cols-[52px_1fr] gap-3 sm:grid-cols-[72px_1fr] sm:gap-4">
          <div className="flex flex-col gap-3">
            {views.map((v, i) => (
              <button
                key={v.label}
                type="button"
                aria-label={v.label}
                onClick={() => setView(i)}
                className={cx('image-frame aspect-[3/4] rounded-[14px] border-2 transition-colors', view === i ? 'border-forest' : 'border-transparent hover:border-line-strong')}
              >
                <img src={product.image} alt="" loading="lazy" className="size-full object-cover" style={v.style} />
              </button>
            ))}
          </div>
          <div className="image-frame relative aspect-[4/5] overflow-hidden rounded-image shadow-soft">
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={view}
                src={product.image}
                alt={productName}
                loading="lazy"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 size-full object-cover"
                style={views[view].style}
              />
            </AnimatePresence>
            {product.badge && <span className="absolute left-4 top-4 rounded-[10px] bg-surface/95 px-2.5 py-1 text-[11.5px] font-medium text-forest backdrop-blur">{t(badgeKey)}</span>}
          </div>
        </div>

        <div className="lg:pt-4">
          <Link to={`/shop?kategoriya=${product.category}`} className="inline-flex min-h-11 items-center text-[12px] font-medium uppercase tracking-[0.16em] text-accent transition-colors hover:text-forest">
            {t(`category_${product.category}`)}
          </Link>
          <h1 className="font-display mt-2 text-[clamp(38px,7vw,54px)] leading-[0.98]">{productName}</h1>
          {productShort && <p className="mt-3 text-[14px] text-muted">{productShort}</p>}
          <p className="mt-6 text-pretty text-[16px] leading-relaxed text-muted">{productDescription}</p>

          <ul className="mt-8 divide-y divide-line border-y border-line">
            {sections.map((section, i) => {
              const isOpen = open === i
              return (
                <li key={section.title}>
                  <button type="button" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : i)} className="flex min-h-14 w-full items-center justify-between gap-4 py-3 text-left text-[15px] font-medium transition-colors hover:text-forest">
                    {section.title}
                    {isOpen ? <Minus className="size-4 shrink-0 text-muted" /> : <Plus className="size-4 shrink-0 text-muted" />}
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
                        <ul className="flex flex-col gap-1.5 pb-5 text-[14px] leading-relaxed text-muted">
                          {section.body.map((line) => <li key={line}>{line}</li>)}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              )
            })}
          </ul>
        </div>
      </div>

      <section className="py-12 md:py-20">
        <h2 className="font-display text-[clamp(30px,5vw,42px)] leading-none">{t('you_may_like')}</h2>
        <div className="mt-8 grid grid-cols-2 gap-x-3 gap-y-8 sm:gap-x-5 md:grid-cols-3 xl:grid-cols-4">
          {related.map((item, index) => <ProductCard key={item.slug} product={item} index={index} />)}
        </div>
      </section>
    </Container>
  )
}
